import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { getShellConfig } from '@src/config/shell/config';
import {
    addDays,
    isAfterDate,
} from '@superbet-group/web.lib.utilities';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { ExperimentsGroup } from '@superbet-group/product.analytics';

const storageKey = 'ftdReminderLastShown';

const numberOfDaysToShowMessage = 5;
const extendedNumberOfDaysToShowMessage = 15;

export function setFtdReminderLastShown() {
    localStorage.setItem(storageKey, new Date().toISOString());
}

export function isFtdReminderShownToday() {
    const lastShown = localStorage.getItem(storageKey);
    if (!lastShown) {
        return false;
    }

    const lastShownDate = new Date(lastShown);
    const today = new Date();

    return lastShownDate.getDate() === today.getDate() && lastShownDate.getMonth() === today.getMonth();
}

export async function shouldShowDepositReminder(rootGetters: any): Promise<boolean> {
    const user = rootGetters['data/authentication/user'];

    if (!user?.registrationDate) {
        return false;
    }

    // the potential candidates for this experiment are the users that:
    // 1. are past the 5 days period of the regular FTD reminder.
    // 2. are within the 15 days period of the extended FTD reminder.
    // 3. have not seen the FTD reminder today.

    const depositConfig = getShellConfig().remoteConfig.wallet.deposit;
    if (!user.hasMadeDeposit && depositConfig.isFtdReminderEnabled && !isFtdReminderShownToday()) {
        const now = new Date();
        const registrationDate = new Date(user.registrationDate.getTime());
        const lastDateForRegularFtdReminder = addDays(numberOfDaysToShowMessage, registrationDate);
        const lastDateForExtendedFtdReminder = addDays(extendedNumberOfDaysToShowMessage, registrationDate);
        if (isAfterDate(lastDateForRegularFtdReminder, now)) {
            // this is the regular FTD reminder, no action needed.
            return true;
        } else if (isAfterDate(lastDateForExtendedFtdReminder, now)) {
            // the user is in the extended FTD reminder period.

            // we need to check if the user is in the experiment group.
            // this utility function is imported async to avoid a nasty circular dependency during the tests.
            const { getFeatureFlagSafely } = await import('@core/utils/featureFlags');
            const isExtendedFtdReminderExperimentEnabled = await getFeatureFlagSafely<boolean | undefined>(
                AccountFlagNames.isExtendedFtdReminderEnabled,
                undefined,
            );

            if (!isExtendedFtdReminderExperimentEnabled !== undefined) {
                AnalyticsSDK.getInstance().log('PE_111_Extended_First_Time_Deposit_Reminder_Experiment_Activated', {
                    group: isExtendedFtdReminderExperimentEnabled ? ExperimentsGroup.B : ExperimentsGroup.A,
                });
            }

            return !!isExtendedFtdReminderExperimentEnabled;
        }
    }

    return false;
}
